/**
 * Configuração do Cliente Axios
 *
 * Este módulo configura e exporta a instância do Axios
 * para comunicação com a API do VendeAi.
 *
 * Funcionalidades:
 * - Base URL configurável
 * - Interceptors para tratamento de erros
 * - Refresh automático de token
 * - Headers padrão
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { friendlyNetworkError } from '@utils/helpers';

/**
 * URL base da API
 * Em desenvolvimento usa proxy do Vite, em produção usa URL real
 */
const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Instância configurada do Axios
 */
export const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Chaves do localStorage
 */
const TOKEN_KEY = 'vendeai_access_token';
const REFRESH_TOKEN_KEY = 'vendeai_refresh_token';

/**
 * Flag para evitar múltiplos refreshs simultâneos
 */
let isRefreshing = false;

/**
 * Fila de requisições aguardando refresh
 */
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}> = [];

/**
 * Processa a fila de requisições após refresh
 */
const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Interceptor de requisição
 * Adiciona o token de autenticação em todas as requisições
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Busca token do localStorage
    const token = localStorage.getItem(TOKEN_KEY);

    // Se existe token, adiciona ao header
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Mensagens em português para erro sem resposta do servidor (#272); ficam em helpers
// para valerem também nas telas que usam o axios direto (portal do cliente)
export { API_NETWORK_MESSAGE, API_TIMEOUT_MESSAGE, friendlyNetworkError } from '@utils/helpers';

/**
 * Interceptor de resposta
 * Trata erros de autenticação e faz refresh automático de token
 */
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    // Sem resposta do servidor: troca o texto do axios (em inglês) por um em português
    // que vale para qualquer tela (#272). No tempo esgotado, a ação pode ter sido feita
    if (!error.response) {
      const friendly = friendlyNetworkError(error);
      if (friendly) error.message = friendly;
    }

    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Assinatura/período de teste expirado: leva à tela de assinatura para reativar a loja
    const errorCode = (error.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
    // O login automático do cadastro trata o 402 na própria tela (não tira a pessoa do cadastro)
    const isSignupLogin =
      window.location.pathname.startsWith('/cadastro') && /\/auth\/login\b/.test(originalRequest?.url || '');
    if (error.response?.status === 402 && errorCode === 'SUBSCRIPTION_EXPIRED' && !isSignupLogin) {
      const path = window.location.pathname;
      if (localStorage.getItem(TOKEN_KEY)) {
        if (!path.startsWith('/assinatura')) {
          window.location.href = '/assinatura';
        }
      } else if (!path.includes('/entrar')) {
        window.location.href = '/entrar?expirado=1';
      }
      return Promise.reject(error);
    }

    // 401 das rotas de entrada (senha errada no login, inclusive o login automático do
    // cadastro) é resposta da própria tela, não sessão vencida: não tenta renovar token
    const isAuthEntryRoute = /\/auth\/(login|register|refresh|forgot-password|reset-password)\b/.test(
      originalRequest?.url || '',
    );

    // Se o erro é 401 (não autenticado) e não é uma retry
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthEntryRoute) {
      // Se já está fazendo refresh, adiciona na fila
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      // Se não tem refresh token, redireciona para login
      if (!refreshToken) {
        // Libera a trava: sem isso, o próximo 401 ficava na fila para sempre (#289:
        // a segunda tentativa com senha errada travava a tela em "carregando")
        isRefreshing = false;
        processQueue(error, null);
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      try {
        // Tenta fazer refresh do token
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        });

        if (response.data.success) {
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;

          // Salva novos tokens
          localStorage.setItem(TOKEN_KEY, accessToken);
          localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);

          // Atualiza header padrão
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

          // Processa fila de requisições pendentes
          processQueue(null, accessToken);

          // Refaz a requisição original
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh falhou, limpa dados e redireciona
        processQueue(refreshError as AxiosError, null);
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Limpa dados de autenticação e redireciona para login
 */
function clearAuthAndRedirect() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('vendeai_user');
  delete api.defaults.headers.common['Authorization'];

  // Redireciona para login se não estiver na página de auth
  if (
    !window.location.pathname.includes('/entrar') &&
    !window.location.pathname.includes('/cadastro')
  ) {
    window.location.href = '/entrar';
  }
}

export default api;
